import { VitrineBadge } from '../components/simulation/VitrineBadge';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { BusinessStatusBadge } from '../components/dashboard';
import { 
  Search, 
  Trash2, 
  Eye, 
  MoreVertical,
  Calendar,
  Loader2,
  Filter,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Send,
  Edit,
  MessageSquare,
  Mail,
  Building2,
  User,
  GitCompare,
  Square,
  CheckSquare,
  Copy,
  UserCog
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import ReassignSimulationDialog from '../components/ReassignSimulationDialog';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    viable: { label: 'Viable', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' },
    risky: { label: 'Risqué', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' },
    'non-viable': { label: 'Non viable', className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' },
  };
  
  const config = statusConfig[status] || statusConfig['non-viable'];
  
  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
};

const AdminStatusBadge = ({ status, language }) => {
  const statusConfig = {
    approved: { 
      label: language === 'fr' ? 'Approuvé' : 'Approved', 
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
      icon: CheckCircle2
    },
    rejected: { 
      label: language === 'fr' ? 'Refusé' : 'Rejected', 
      className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
      icon: XCircle
    },
    pending_review: { 
      label: language === 'fr' ? 'En révision' : 'Under Review', 
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
      icon: RefreshCw
    },
  };
  
  if (!status) return null;
  
  const config = statusConfig[status];
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.className} gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

export default function HistoryPage() {
  const { api, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAdminStatus, setFilterAdminStatus] = useState('all');
  
  // Dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [reviewMessage, setReviewMessage] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Compare mode states
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const dateLocale = language === 'fr' ? fr : enUS;
  
  // Check if user has admin/manager role
  const canManage = ['admin', 'super_admin', 'dg', 'dop', 'directeur_regional', 'directeur_commercial'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [reassignTarget, setReassignTarget] = useState(null);

  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    try {
      const response = await api().get('/simulations');
      setSimulations(response.data);
    } catch (error) {
      console.error('Failed to fetch simulations:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'fr' ? 'Voulez-vous vraiment supprimer cette simulation ?' : 'Are you sure you want to delete this simulation?')) {
      return;
    }
    
    try {
      await api().delete(`/simulations/${id}`);
      setSimulations(simulations.filter(s => s.id !== id));
      toast.success(language === 'fr' ? 'Simulation supprimée' : 'Simulation deleted');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(t('error'));
    }
  };

  // Quick-clone a simulation from the history list to instantly build variants
  // (different CA, commission, food cost, etc.) without opening the original.
  const handleDuplicate = async (sim) => {
    try {
      const response = await api().post(`/simulations/${sim.id}/duplicate`);
      const newSimId = response.data?.id;
      toast.success(
        language === 'fr'
          ? `Simulation « ${sim.restaurant_name || sim.id?.slice(0, 8)} » dupliquée`
          : `Simulation duplicated`
      );
      if (newSimId) {
        navigate(`/simulation/${newSimId}`);
      } else {
        fetchSimulations();
      }
    } catch (error) {
      console.error('Failed to duplicate:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (language === 'fr' ? 'Erreur lors de la duplication' : 'Failed to duplicate'));
    }
  };

  const handleRequestReview = async () => {
    if (!selectedSimulation) return;
    
    setActionLoading(true);
    try {
      await api().post(`/simulations/${selectedSimulation.id}/request-review`, {
        message: reviewMessage
      });
      toast.success(language === 'fr' ? 'Demande de révision envoyée' : 'Review request sent');
      setReviewDialogOpen(false);
      setReviewMessage('');
      fetchSimulations();
    } catch (error) {
      console.error('Failed to request review:', error);
      toast.error(t('error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedSimulation || !newStatus) return;
    
    setActionLoading(true);
    try {
      await api().put(`/simulations/${selectedSimulation.id}/status`, {
        status: newStatus,
        comment: statusComment
      });
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusComment('');
      fetchSimulations();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(t('error'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle business status change (Gagné/Perdu/En cours)
  const [businessStatusLoading, setBusinessStatusLoading] = useState(null);
  
  const handleBusinessStatusChange = async (simId, newBusinessStatus) => {
    setBusinessStatusLoading(simId);
    try {
      await api().patch(`/simulations/${simId}/business-status`, {
        business_status: newBusinessStatus
      });
      
      // Update local state
      setSimulations(prev => prev.map(sim => 
        sim.id === simId 
          ? { ...sim, business_status: newBusinessStatus }
          : sim
      ));
      
      const statusLabels = {
        won: language === 'fr' ? 'Dossier Gagné' : 'Won',
        lost: language === 'fr' ? 'Dossier Perdu' : 'Lost',
        in_progress: language === 'fr' ? 'En cours' : 'In Progress'
      };
      
      toast.success(language === 'fr' 
        ? `Statut changé en "${statusLabels[newBusinessStatus]}"` 
        : `Status changed to "${statusLabels[newBusinessStatus]}"`
      );
    } catch (error) {
      console.error('Failed to update business status:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || (language === 'fr' ? 'Erreur lors du changement de statut' : 'Failed to update status'));
    } finally {
      setBusinessStatusLoading(null);
    }
  };

  const openReviewDialog = (sim) => {
    setSelectedSimulation(sim);
    setReviewMessage('');
    setReviewDialogOpen(true);
  };

  const openStatusDialog = (sim) => {
    setSelectedSimulation(sim);
    setNewStatus(sim.admin_status || '');
    setStatusComment('');
    setStatusDialogOpen(true);
  };

  // Compare mode handlers
  const toggleCompareMode = () => {
    setCompareMode(!compareMode);
    setSelectedForCompare([]);
  };

  const toggleSelectForCompare = (simId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(simId)) {
        return prev.filter(id => id !== simId);
      }
      if (prev.length >= 3) {
        toast.warning(language === 'fr' ? 'Maximum 3 simulations' : 'Maximum 3 simulations');
        return prev;
      }
      return [...prev, simId];
    });
  };

  const handleCompare = () => {
    if (selectedForCompare.length < 2) {
      toast.error(language === 'fr' ? 'Sélectionnez au moins 2 simulations' : 'Select at least 2 simulations');
      return;
    }
    navigate(`/compare?ids=${selectedForCompare.join(',')}`);
  };

  // Quick GO/NO GO for super admin
  const handleQuickDecision = async (sim, decision) => {
    setActionLoading(true);
    try {
      await api().put(`/simulations/${sim.id}/status`, {
        status: decision,
        comment: decision === 'approved' ? 'GO - Projet validé' : 'NO GO - Projet refusé'
      });
      toast.success(
        decision === 'approved' 
          ? (language === 'fr' ? '✅ GO - Projet validé' : '✅ GO - Project approved')
          : (language === 'fr' ? '❌ NO GO - Projet refusé' : '❌ NO GO - Project rejected')
      );
      fetchSimulations();
    } catch (error) {
      console.error('Failed to update decision:', error);
      toast.error(t('error'));
    } finally {
      setActionLoading(false);
    }
  };

  // Get row background color based on admin status
  const getRowClassName = (sim) => {
    let baseClass = "cursor-pointer transition-colors ";
    if (sim.admin_status === 'approved') {
      return baseClass + "bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30 border-l-4 border-l-emerald-500";
    } else if (sim.admin_status === 'rejected') {
      return baseClass + "bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 border-l-4 border-l-red-500";
    } else if (sim.admin_status === 'pending_review') {
      return baseClass + "bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 border-l-4 border-l-amber-500";
    }
    return baseClass + "hover:bg-slate-50 dark:hover:bg-slate-800/50";
  };

  // Filter simulations
  const filteredSimulations = simulations.filter(sim => {
    const matchesSearch = 
      sim.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sim.retailer_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || sim.status === filterStatus;
    const matchesAdminStatus = filterAdminStatus === 'all' || sim.admin_status === filterAdminStatus;
    
    return matchesSearch && matchesStatus && matchesAdminStatus;
  });

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="history-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
              {t('simulationHistory')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {language === 'fr' ? `${simulations.length} études CAPEX` : `${simulations.length} CAPEX studies`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {compareMode && selectedForCompare.length >= 2 && (
              <Button 
                onClick={handleCompare}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="compare-selected-btn"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                {language === 'fr' ? `Comparer (${selectedForCompare.length})` : `Compare (${selectedForCompare.length})`}
              </Button>
            )}
            <Button 
              variant={compareMode ? "default" : "outline"}
              onClick={toggleCompareMode}
              data-testid="toggle-compare-mode"
            >
              {compareMode ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </>
              ) : (
                <>
                  <GitCompare className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Comparer' : 'Compare'}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher par nom ou enseigne...' : 'Search by name or retailer...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="viable">Viable</SelectItem>
                  <SelectItem value="risky">{language === 'fr' ? 'Risqué' : 'Risky'}</SelectItem>
                  <SelectItem value="non-viable">{language === 'fr' ? 'Non viable' : 'Non-viable'}</SelectItem>
                </SelectContent>
              </Select>
              {canManage && (
                <Select value={filterAdminStatus} onValueChange={setFilterAdminStatus}>
                  <SelectTrigger className="w-full md:w-44" data-testid="filter-admin-status">
                    <SelectValue placeholder="Décision" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'fr' ? 'Toutes décisions' : 'All decisions'}</SelectItem>
                    <SelectItem value="approved">{language === 'fr' ? 'Approuvé' : 'Approved'}</SelectItem>
                    <SelectItem value="rejected">{language === 'fr' ? 'Refusé' : 'Rejected'}</SelectItem>
                    <SelectItem value="pending_review">{language === 'fr' ? 'En révision' : 'Under review'}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Simulations Table */}
        <Card className="border border-slate-200 dark:border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredSimulations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500">{language === 'fr' ? 'Aucune simulation trouvée' : 'No simulations found'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {compareMode && (
                        <TableHead className="w-12">
                          <span className="sr-only">Select</span>
                        </TableHead>
                      )}
                      <TableHead>{language === 'fr' ? 'Projet' : 'Project'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Enseigne' : 'Retailer'}</TableHead>
                      {isSuperAdmin && <TableHead>{language === 'fr' ? 'Créateur' : 'Creator'}</TableHead>}
                      <TableHead className="text-right">CA Potentiel</TableHead>
                      <TableHead className="text-right">CAPEX</TableHead>
                      <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Dossier' : 'Deal'}</TableHead>
                      {canManage && <TableHead>{language === 'fr' ? 'Décision' : 'Decision'}</TableHead>}
                      <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSimulations.map((sim) => (
                      <TableRow 
                        key={sim.id} 
                        className={`${getRowClassName(sim)} ${compareMode && selectedForCompare.includes(sim.id) ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                        data-testid={`simulation-row-${sim.id}`}
                      >
                        {compareMode && (
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedForCompare.includes(sim.id)}
                              onCheckedChange={() => toggleSelectForCompare(sim.id)}
                              data-testid={`compare-checkbox-${sim.id}`}
                            />
                          </TableCell>
                        )}
                        <TableCell 
                          className="font-medium"
                          onClick={() => navigate(`/simulation/${sim.id}`)}
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {sim.project_name}
                            <VitrineBadge format={sim.format} testid={`vitrine-badge-${sim.id}`} />
                          </div>
                          {sim.review_requested && (
                            <Badge variant="outline" className="text-xs mt-1 border-amber-400 text-amber-600">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {language === 'fr' ? 'Révision demandée' : 'Review requested'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/simulation/${sim.id}`)}>
                          {sim.retailer_name}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell onClick={() => navigate(`/simulation/${sim.id}`)}>
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {sim.creator_name || '-'}
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="text-right font-mono" onClick={() => navigate(`/simulation/${sim.id}`)}>
                          {formatCurrency(sim.ca_potential_ht)}
                        </TableCell>
                        <TableCell className="text-right font-mono" onClick={() => navigate(`/simulation/${sim.id}`)}>
                          {formatCurrency(sim.total_capex)}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/simulation/${sim.id}`)}>
                          <StatusBadge status={sim.status} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <BusinessStatusBadge 
                            simulation={sim} 
                            language={language}
                            editable={canManage}
                            onStatusChange={handleBusinessStatusChange}
                            isLoading={businessStatusLoading === sim.id}
                          />
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            {isSuperAdmin && !sim.admin_status ? (
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500"
                                  onClick={(e) => { e.stopPropagation(); handleQuickDecision(sim, 'approved'); }}
                                  disabled={actionLoading}
                                >
                                  GO
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 px-2 text-xs bg-red-500 hover:bg-red-600 text-white border-red-500"
                                  onClick={(e) => { e.stopPropagation(); handleQuickDecision(sim, 'rejected'); }}
                                  disabled={actionLoading}
                                >
                                  NO GO
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <AdminStatusBadge status={sim.admin_status} language={language} />
                                {isSuperAdmin && sim.admin_status && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => { e.stopPropagation(); openStatusDialog(sim); }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-slate-500 text-sm" onClick={() => navigate(`/simulation/${sim.id}`)}>
                          {format(new Date(sim.created_at), 'dd/MM/yyyy', { locale: dateLocale })}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/simulation/${sim.id}`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Voir' : 'View'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/simulation/${sim.id}`)}>
                                <Edit className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Modifier' : 'Edit'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDuplicate(sim)}
                                data-testid={`duplicate-sim-${sim.id}`}
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Cloner / Variante' : 'Clone / Variant'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openReviewDialog(sim)}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Demander révision' : 'Request review'}
                              </DropdownMenuItem>
                              {canManage && (
                                <DropdownMenuItem onClick={() => openStatusDialog(sim)}>
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Changer décision' : 'Change decision'}
                                </DropdownMenuItem>
                              )}
                              {isAdmin && (
                                <DropdownMenuItem onClick={() => setReassignTarget(sim)} data-testid={`reassign-item-${sim.id}`}>
                                  <UserCog className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Réaffecter' : 'Reassign'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDelete(sim.id)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {language === 'fr' ? 'Supprimer' : 'Delete'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review Request Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="w-full sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Demander une révision' : 'Request a review'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? `Un email sera envoyé au créateur de l'étude "${selectedSimulation?.project_name}".`
                  : `An email will be sent to the creator of "${selectedSimulation?.project_name}".`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="review-message">
                  {language === 'fr' ? 'Message (optionnel)' : 'Message (optional)'}
                </Label>
                <Textarea
                  id="review-message"
                  placeholder={language === 'fr' ? 'Expliquez les points à revoir...' : 'Explain what needs to be reviewed...'}
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleRequestReview} disabled={actionLoading} className="gap-2">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {language === 'fr' ? 'Envoyer' : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="w-full sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Modifier la décision' : 'Update decision'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? `Changer le statut de l'étude "${selectedSimulation?.project_name}".`
                  : `Change the status of "${selectedSimulation?.project_name}".`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nouvelle décision' : 'New decision'}</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        {language === 'fr' ? 'Approuver' : 'Approve'}
                      </div>
                    </SelectItem>
                    <SelectItem value="rejected">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        {language === 'fr' ? 'Refuser' : 'Reject'}
                      </div>
                    </SelectItem>
                    <SelectItem value="pending_review">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-amber-600" />
                        {language === 'fr' ? 'En révision' : 'Under review'}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status-comment">
                  {language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}
                </Label>
                <Textarea
                  id="status-comment"
                  placeholder={language === 'fr' ? 'Ajouter un commentaire...' : 'Add a comment...'}
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleStatusUpdate} disabled={actionLoading || !newStatus} className="gap-2">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {language === 'fr' ? 'Confirmer' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ReassignSimulationDialog
          sim={reassignTarget}
          open={!!reassignTarget}
          onOpenChange={(o) => !o && setReassignTarget(null)}
          onDone={(d) => {
            setSimulations(prev => prev.map(s => s.id === reassignTarget?.id
              ? { ...s, creator_name: d.creator_name, creator_email: d.creator_email }
              : s));
            setReassignTarget(null);
          }}
        />
      </div>
    </AppLayout>
  );
}
